import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';

interface SuccessModalProps {
  open: boolean;
  onClose: () => void;
  type: 'success' | 'error';
  title: string;
  message: string;
  onNavigateToDashboard?: () => void;
}

export default function SuccessModal({
  open,
  onClose,
  type,
  title,
  message,
  onNavigateToDashboard
}: SuccessModalProps) {
  const isSuccess = type === 'success';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          overflow: 'visible',
          position: 'relative'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)'
        }
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          }
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 6, textAlign: 'center' }}>
        {/* Icon */}
        <Box sx={{ mb: 3 }}>
          {isSuccess ? (
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: '#10b981',
                filter: 'drop-shadow(0 4px 8px rgba(16, 185, 129, 0.3))'
              }}
            />
          ) : (
            <ErrorIcon
              sx={{
                fontSize: 80,
                color: '#ef4444',
                filter: 'drop-shadow(0 4px 8px rgba(239, 68, 68, 0.3))'
              }}
            />
          )}
        </Box>

        {/* Status Chip */}
        <Chip
          label={isSuccess ? 'SUCCESS' : 'ERROR'}
          sx={{
            mb: 2,
            px: 2,
            py: 0.5,
            fontSize: '0.75rem',
            fontWeight: 700,
            letterSpacing: '1px',
            background: isSuccess
              ? 'linear-gradient(135deg, #10b981, #059669)'
              : 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: 'white',
            border: 'none'
          }}
        />

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: '#1e293b',
            background: isSuccess
              ? 'linear-gradient(135deg, #10b981, #059669)'
              : 'linear-gradient(135deg, #ef4444, #dc2626)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          {title}
        </Typography>

        {/* Message */}
        <Typography
          variant="body1"
          sx={{
            color: '#64748b',
            mb: 4,
            lineHeight: 1.6,
            maxWidth: '400px',
            mx: 'auto'
          }}
        >
          {message}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {isSuccess && onNavigateToDashboard && (
            <Button
              variant="contained"
              size="large"
              startIcon={<DashboardIcon />}
              onClick={onNavigateToDashboard}
              sx={{
                background: 'linear-gradient(135deg, #2e86ab, #4a90c2)',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(46, 134, 171, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #266b8a, #3d7ba3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(46, 134, 171, 0.4)'
                }
              }}
            >
              Go to Dashboard
            </Button>
          )}

          <Button
            variant="outlined"
            size="large"
            onClick={onClose}
            sx={{
              borderColor: '#e2e8f0',
              color: '#64748b',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              '&:hover': {
                borderColor: '#cbd5e1',
                backgroundColor: '#f8fafc'
              }
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}